import { Flex, Skeleton, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import type { ProductThreshold } from '@iheartjane/types';
import get from 'lodash-es/get';
import { useCallback, useMemo } from 'react';

import { BrandConditions } from '@jane/business-admin/components/shared/specials/modal/conditions/brandConditions/BrandConditions';
import { CategoryConditions } from '@jane/business-admin/components/shared/specials/modal/conditions/categoryConditions/CategoryConditions';
import { convertRulesToString } from '@jane/business-admin/components/shared/specials/modal/conditions/convertRulesToString';
import { LoadingCondition } from '@jane/business-admin/components/shared/specials/modal/conditions/generic_components/LoadingCondition';
import { PriceConditions } from '@jane/business-admin/components/shared/specials/modal/conditions/PriceConditions';
import {
  ConditionType,
  FORM_FIELD_DEFAULT_MARGIN,
} from '@jane/business-admin/components/shared/specials/modal/form';
import { BODY_LINE_HEIGHT } from '@jane/business-admin/util';

import type { FormValues } from './form.types';
import { MenuRowCard } from './MenuRowModal';

export interface MenuRowConditionsCardProps {
  isLoading: boolean;
}

// Empty prefix since MenuRowConditionsCard uses top-level form fields (rules, apply_to)
const MENU_ROW_FIELD_PREFIX = '';

export const applyToOptions = [
  {
    id: 'advanced',
    label: 'Category, brand, and price',
    value: 'advanced',
  },
  {
    id: 'products',
    label: 'Individual products',
    value: 'products',
  },
];

export const MenuRowConditionsCard = ({
  isLoading,
}: MenuRowConditionsCardProps) => {
  const { setValue, watch, getValues } = useFormContext<FormValues>();

  const rulesFieldPath = 'rules';
  const applyToFieldPath = 'apply_to';

  const rulesWatch = watch(rulesFieldPath);
  const applyToWatch = watch(applyToFieldPath);

  const prices = useMemo(() => {
    return get(rulesWatch, 'product_threshold') || {};
  }, [convertRulesToString(rulesWatch)]);

  const onPricesUpdate = useCallback(
    (prices: ProductThreshold) => {
      setValue(`${rulesFieldPath}.product_threshold`, prices, {
        shouldDirty: true,
      });
    },
    [rulesFieldPath, setValue]
  );

  const onCategoriesUpdate = useCallback(
    (kinds: any, includesOrExcludes: 'includes' | 'excludes') => {
      const currentRules = getValues(rulesFieldPath);
      const rules =
        currentRules && currentRules[includesOrExcludes]
          ? currentRules[includesOrExcludes]
          : null;

      const firstCondition = rules?.[0] ?? {};
      const newRules = {
        ...currentRules,
        [includesOrExcludes]: [{ ...firstCondition, kinds }],
      };

      setValue(rulesFieldPath, newRules, { shouldDirty: true });
    },
    [rulesFieldPath, getValues, setValue]
  );

  return (
    <MenuRowCard>
      <Flex flexDirection="column">
        <Typography
          variant="header-bold"
          branded
          mb={FORM_FIELD_DEFAULT_MARGIN}
        >
          Conditions
        </Typography>
        <Flex flexDirection="column" mb={FORM_FIELD_DEFAULT_MARGIN}>
          <Typography variant="body-bold" mb={FORM_FIELD_DEFAULT_MARGIN}>
            Apply to
          </Typography>
          {isLoading ? (
            <Skeleton direction="row" animate>
              <Skeleton.Bone height={BODY_LINE_HEIGHT} width="40%" />
              <Skeleton.Bone height={BODY_LINE_HEIGHT} ml={12} width="40%" />
            </Skeleton>
          ) : (
            <Form.RadioFieldGroup
              shouldUnregister={false}
              name={applyToFieldPath}
              row={true}
              options={applyToOptions}
            />
          )}
        </Flex>
        {isLoading ? (
          <>
            <LoadingCondition />
            <LoadingCondition />
            <LoadingCondition />
          </>
        ) : (
          <>
            {applyToWatch === 'advanced' && (
              <>
                <CategoryConditions
                  fieldPrefix={MENU_ROW_FIELD_PREFIX}
                  isGlobalSpecial={false}
                  type={ConditionType.Categories}
                  onConditionsUpdate={onCategoriesUpdate}
                />
                <BrandConditions
                  fieldPrefix={MENU_ROW_FIELD_PREFIX}
                  isGlobalSpecial={false}
                  type={ConditionType.Brands}
                />
                <PriceConditions
                  fieldPrefix={MENU_ROW_FIELD_PREFIX}
                  conditions={prices}
                  onConditionsUpdate={onPricesUpdate}
                  type={ConditionType.Price}
                />
              </>
            )}
          </>
        )}
      </Flex>
    </MenuRowCard>
  );
};
